import redCheckbox from "../../../assets//svg/red_cross_box.svg";
import greenCheckbox from "../../../assets/svg/green_check_box.svg";
import { NULLABLE_VALUES, YesNo } from "../../../constants";
import {
  Ca100Package,
  Coverage,
  Na100Package,
  Provider,
  PublicMetric,
  TargetStatus,
} from "../../../models/CompanyprofileModels";
import { DataEntry } from "../../../components/tables/FlexibleTable";
import {
  DEFAULT_LABEL,
  LegendAccordionPayload,
  providerTabs,
  RADIAL_COLORS,
  SBTI_TARGET_STATUS,
  SBTIColumn,
  SBTIMetric,
  TITLES,
  TPI_QUESTION_LABELS,
  TPILevelMetric,
} from "./constants";
import { TagProps } from "../../../components/visuals/CompassTag";
import { TPI_LEVEL_METRICS } from "./constants";
import { isNotNan } from "../../../utils/formattingUtils";
import {
  CA100_DISCLOSURE_TREE,
  CA100_disclosureMetricLevels,
  CA100_disclosureMetrics,
  CA100_lobbyingMetrics,
  DISCLOSURE_PROGRESS_METRIC_STUFFIX,
  DisclosureTreeNode,
  FormattedCA100Metric,
  LOBBYING_BASE_REGEX,
  LOBBYING_CHILD_NODE_REGEX,
  PERCENTAGE_TO_BADGE_COLORS_MAPPING,
} from "./CA100Constants";
import {
  FormattedNA100Metric,
  NA100_metrics,
  NA100_metricLevels,
} from "./NA100Constants";
import { MESSAGES } from "../../../messages";

export enum CoverageStatusTitle {
  INCLUDED = "Included",
  NOT_COVERED = "Not Covered",
}

type CoverageStatus = {
  checkbox: string;
  title: CoverageStatusTitle;
};

export const getStatus = (isChecked: boolean): CoverageStatus => {
  return isChecked
    ? {
        checkbox: greenCheckbox,
        title: CoverageStatusTitle.INCLUDED,
      }
    : {
        checkbox: redCheckbox,
        title: CoverageStatusTitle.NOT_COVERED,
      };
};

export const getMetricValue = (
  metricId: string,
  metricsData: PublicMetric[],
): string | null | number => {
  const metricObj: PublicMetric | null =
    metricsData.find((item) => item.metric_id === metricId) || null;

  const value = metricObj?.Is_Numeric
    ? metricObj.value_numeric
    : metricObj?.value_text;

  return value || null;
};

export const isPackageDataAllNull = (
  dataProvider: string,
  metricsData: PublicMetric[],
): boolean => {
  const relevantMetrics = metricsData.filter(
    (metric) => metric.Data_Provider === dataProvider,
  );

  if (relevantMetrics.length === 0) return true;

  return relevantMetrics.every((metric) => {
    const value = metric.Is_Numeric ? metric.value_numeric : metric.value_text;
    return NULLABLE_VALUES.includes(String(value));
  });
};

export const buildCoverageData = (
  metricsData: PublicMetric[],
): Record<Provider, Coverage> => {
  const coverageData: Record<Provider, Coverage> = {
    SBTI: {
      isCovered: !isPackageDataAllNull(Provider.SBTI, metricsData),
      title: providerTabs.SBTI.title,
    },
    TPI: {
      isCovered: !isPackageDataAllNull(Provider.TPI, metricsData),
      title: providerTabs.TPI.title,
    },
    CA100: {
      isCovered: !isPackageDataAllNull(Provider.CA100, metricsData),
      title: providerTabs.CA100.title,
    },
    NZIF: {
      isCovered: !isPackageDataAllNull(Provider.NZIF, metricsData),
      title: providerTabs.NZIF.title,
    },
    NA100: {
      isCovered: !isPackageDataAllNull(Provider.NA100, metricsData),
      title: providerTabs.NA100.title,
    },
  };

  return coverageData;
};

export const getSbtiTargetStatus = (statusKey: TargetStatus): TagProps => {
  return (
    (SBTI_TARGET_STATUS.find(
      (status) => status.tagKey === statusKey,
    ) as TagProps) || SBTI_TARGET_STATUS[3] // Default to the N/A status if not found
  );
};

export const buildSbtiDataEntries = (
  metricsData: PublicMetric[],
): DataEntry[] => {
  const nearTerm: DataEntry = {
    [SBTIColumn.NEAR_TERM_TARGET_STATUS]: getMetricValue(
      SBTIMetric.Near_term_Target_Status,
      metricsData,
    ) as TargetStatus,
    [SBTIColumn.TARGET_YEAR]: getMetricValue(
      SBTIMetric.Near_term_Target_Year,
      metricsData,
    ),
    [SBTIColumn.TARGET_IMPLIED_TEMPERATURE]: getMetricValue(
      SBTIMetric.Near_term_Target_Implied_Temperature,
      metricsData,
    ),
  };

  const longTerm: DataEntry = {
    [SBTIColumn.LONG_TERM_TARGET_STATUS]: getMetricValue(
      SBTIMetric.Long_term_Target_Status,
      metricsData,
    ),
    [SBTIColumn.TARGET_YEAR]: getMetricValue(
      SBTIMetric.Long_term_Target_Year,
      metricsData,
    ),
    [SBTIColumn.TARGET_IMPLIED_TEMPERATURE]: getMetricValue(
      SBTIMetric.Long_term_Target_Implied_Temperature,
      metricsData,
    ),
  };

  const netZero: DataEntry = {
    [SBTIColumn.NET_ZERO_STATUS]: getMetricValue(
      SBTIMetric.Net_Zero_Committed,
      metricsData,
    ),
    [SBTIColumn.TARGET_YEAR]: getMetricValue(
      SBTIMetric.Net_Zero_Year,
      metricsData,
    ),
  };

  return [nearTerm, longTerm, netZero];
};

export const buildTPILevelMetrics = (
  metricsData: PublicMetric[],
): TPILevelMetric[][] => {
  return TPI_LEVEL_METRICS.map((levelMetrics) =>
    levelMetrics.map((metric) => {
      const metricValue = getMetricValue(metric, metricsData);
      const valueBoolean = isNotNan(metricValue) && metricValue !== YesNo.NO;
      return {
        metric_id: metric,
        value: metricValue,
        valueBoolean,
      };
    }),
  );
};

export const buildRadialMatrix = (metricsData: PublicMetric[]): number[][] => {
  return TPI_LEVEL_METRICS.map((levelMetrics) =>
    levelMetrics.map((metricId) => {
      const value = getMetricValue(metricId, metricsData);
      const isAffirmative = isNotNan(value) && value !== YesNo.NO;
      return isAffirmative ? 1 : 0;
    }),
  );
};

const getLevelTitle = (level: number): string => {
  return TITLES[level] ?? `Level ${level}`;
};

export const buildLegendAccordionPayload = (
  levelData: TPILevelMetric[][],
): LegendAccordionPayload[] => {
  const radialColors = RADIAL_COLORS.map((color) => color.filled);
  return levelData.map((metrics, index) => ({
    level: index,
    color: radialColors[index] ?? "#ccc",
    title: getLevelTitle(index),
    questions: metrics.map((metric) => ({
      id: metric.metric_id,
      text: TPI_QUESTION_LABELS[metric.metric_id] ?? DEFAULT_LABEL,
      status: metric.valueBoolean,
    })),
  }));
};

export const getCA100Metric = <
  T extends CA100_disclosureMetrics | CA100_lobbyingMetrics,
>(
  metricId: T,
  dataPackage: Ca100Package,
  metricsData: PublicMetric[],
): FormattedCA100Metric<T> => {
  const metricObj: PublicMetric | null =
    metricsData.find(
      (item) =>
        item.metric_id === metricId && item.Data_Package === dataPackage,
    ) || null;

  return {
    metricId,
    value: metricObj?.value_text || MESSAGES.NOT_AVAILABLE,
    metricName:
      metricObj?.Compass_Metric_Display_Name || MESSAGES.NOT_AVAILABLE,
    metricDescription:
      metricObj?.Compass_Metric_Description || MESSAGES.NOT_AVAILABLE,
  };
};

export const collectCA100Metrics = (
  dataPackage: Ca100Package,
  metricsData: PublicMetric[],
): FormattedCA100Metric<CA100_disclosureMetrics>[] => {
  const results: FormattedCA100Metric<CA100_disclosureMetrics>[] = [];

  const traverse = (nodes: DisclosureTreeNode[]): void => {
    for (const node of nodes) {
      results.push(getCA100Metric(node.id, dataPackage, metricsData));

      if (node.progress) {
        results.push(getCA100Metric(node.progress, dataPackage, metricsData));
      }

      if (node.children) {
        traverse(node.children);
      }
    }
  };

  traverse(CA100_DISCLOSURE_TREE);
  return results;
};

export const buildCADisclosureData = (
  metricsData: PublicMetric[],
): FormattedCA100Metric<CA100_disclosureMetrics>[] => {
  const level1Metrics: CA100_disclosureMetrics[] = Object.entries(
    CA100_disclosureMetricLevels,
  )
    .filter(
      ([key, level]) =>
        level === 1 && !key.includes(DISCLOSURE_PROGRESS_METRIC_STUFFIX),
    )
    .map(([metric]) => metric as CA100_disclosureMetrics);

  return level1Metrics.map((metric) =>
    getCA100Metric(metric, Ca100Package.DISCLOSURE, metricsData),
  );
};

export const getBadgeColorsFromPercentage = (
  value: number,
): { bg: string; text: string } => {
  if (value === 0) {
    return PERCENTAGE_TO_BADGE_COLORS_MAPPING.na;
  }
  if (value < 25) {
    return PERCENTAGE_TO_BADGE_COLORS_MAPPING.low;
  } else if (value < 75) {
    return PERCENTAGE_TO_BADGE_COLORS_MAPPING.mid;
  } else {
    return PERCENTAGE_TO_BADGE_COLORS_MAPPING.high;
  }
};

export const fallbackRetrievePct = (input: string, isChild = false): number => {
  const regex = isChild ? LOBBYING_CHILD_NODE_REGEX : LOBBYING_BASE_REGEX;

  const match = input.match(regex);

  return match ? parseFloat(match[1]) : 0;
};

// NA100 Functions
export const getNA100Metric = <T extends NA100_metrics>(
  metricId: T,
  dataPackage: Na100Package,
  metricsData: PublicMetric[],
): FormattedNA100Metric<T> => {
  const metricObj: PublicMetric | null =
    metricsData.find(
      (item) =>
        item.metric_id === metricId && item.Data_Package === dataPackage,
    ) || null;

  return {
    metricId,
    value: metricObj?.value_text || MESSAGES.NOT_AVAILABLE,
    metricName:
      metricObj?.Compass_Metric_Display_Name || MESSAGES.NOT_AVAILABLE,
    metricDescription:
      metricObj?.Compass_Metric_Description || MESSAGES.NOT_AVAILABLE,
  };
};

export const buildNA100Data = (
  metricsData: PublicMetric[],
): FormattedNA100Metric<NA100_metrics>[] => {
  const level1Metrics: NA100_metrics[] = Object.entries(NA100_metricLevels)
    .filter(([, level]) => level === 1)
    .map(([metric]) => metric as NA100_metrics);

  return level1Metrics.map((metric) =>
    getNA100Metric(metric, Na100Package.NA100, metricsData),
  );
};
